import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sprout, Wrench } from 'lucide-react';
import type { Readiness } from '../types/api';

// LES-18: how finished a lesson is. Stable lessons have no badge. The public lesson pages say the same
// (backend/app/public/pages.py READINESS).
const READINESS: Record<Exclude<Readiness, 'stable'>, { label: string; explanation: string; className: string; Icon: typeof Sprout }> = {
  preview: {
    label: 'Preview',
    explanation: 'An early lesson: it works, but its text, code or grading may still change.',
    className: 'badge-gray border-dashed',
    Icon: Sprout,
  },
  beta: {
    label: 'Beta',
    explanation: "Tested end to end; we're still polishing it.",
    className: 'badge-cyan',
    Icon: Wrench,
  },
};

const PANEL_WIDTH = 256;

// A lesson's readiness badge, beside its difficulty. Its explanation shows on hover and opens on click or tap (closes on
// Escape, a click elsewhere or scrolling). The panel is drawn over the page (a portal), so cards that clip their content
// and the top bar don't cut it off. `plain`: inside a button or label, where a button can't go, only a tooltip.
const ReadinessBadge: React.FC<{ readiness?: Readiness | null; plain?: boolean }> = ({ readiness, plain = false }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const shown = open || hovered;

  useLayoutEffect(() => {
    if (!shown || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 32);
    setPosition({ top: rect.bottom + 6, left: Math.max(16, Math.min(rect.left, window.innerWidth - 16 - width)) });
  }, [shown]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!buttonRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!readiness || readiness === 'stable' || !READINESS[readiness]) return null;
  const { label, explanation, className, Icon } = READINESS[readiness];

  if (plain) {
    return (
      <span className={`badge ${className}`} title={explanation} data-testid="readiness-badge">
        <Icon aria-hidden="true" />
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid="readiness-badge"
        // Lesson cards open the lesson on click: the badge only explains itself.
        onClick={(event) => { event.stopPropagation(); setOpen(!open); }}
        onKeyDown={(event) => event.stopPropagation()}
        onPointerEnter={(event) => { if (event.pointerType === 'mouse') setHovered(true); }}
        onPointerLeave={() => setHovered(false)}
        aria-expanded={open}
        aria-describedby={shown ? panelId : undefined}
        className={`badge ${className} cursor-help`}
      >
        <Icon aria-hidden="true" />
        {label}
      </button>
      {shown && position && createPortal(
        <div
          id={panelId}
          role="tooltip"
          style={{ top: position.top, left: position.left, width: Math.min(PANEL_WIDTH, window.innerWidth - 32) }}
          className="fixed z-[60] rounded-lg border border-gray-200 bg-white p-3 text-sm normal-case tracking-normal text-gray-700 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
          {explanation}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ReadinessBadge;
